import React, { useEffect, useState, useCallback } from "react"
import axios from "axios"
import { useActiveClinic } from "../../../context/ClinicContext"
import { useClinicPlanSnapshot } from "../../subscriptions/hooks/useClinicPlanSnapshot"
import { useClinicSubscription } from "../../subscriptions/hooks/useClinicSubscription"
import "../../../shared/styles/settings-system.css"

export default function DoctorsPage() {
  const { activeClinicId } = useActiveClinic()

  const [doctors, setDoctors] = useState([])
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)

  const { data: planSnapshot } = useClinicPlanSnapshot(activeClinicId)
  const { subscription } = useClinicSubscription(activeClinicId)

  const maxDoctors = planSnapshot?.plan?.maxDoctors
  const doctorsCount = planSnapshot?.usage?.doctorsCount ?? 0

  const limitReached =
    maxDoctors !== null &&
    maxDoctors !== undefined &&
    doctorsCount >= maxDoctors

  const isReadOnly = subscription?.access?.isReadOnly
  const readOnlyReason = subscription?.access?.reason

  async function handleCreate() {
    try {
      setSaving(true)
      setError(null)

      await axios.post("/api/multi-clinic/add-doctor", {
        clinicId: activeClinicId,
        name,
        email
      })

      setName("")
      setEmail("")

      await loadDoctors()
    } catch (err) {
      const message =
        err?.response?.data?.message || "No se pudo crear el doctor"

      setError(Array.isArray(message) ? message[0] : message)
    } finally {
      setSaving(false)
    }
  }

  const loadDoctors = useCallback(async () => {
    if (!activeClinicId || activeClinicId === "ALL") return

    const { data } = await axios.get("/api/multi-clinic/clinic-doctors", {
      params: { clinicId: activeClinicId }
    })

    setDoctors(Array.isArray(data) ? data : [])
  }, [activeClinicId])

  useEffect(() => {
    loadDoctors()
  }, [loadDoctors])

  const canCreate =
    !!activeClinicId &&
    activeClinicId !== "ALL" &&
    !saving &&
    !limitReached &&
    !isReadOnly &&
    !!name.trim() &&
    !!email.trim()

  return (
    <div className="of-settings-page">
      <div className="of-settings-hero">
        <h1 className="of-settings-hero__title">Doctores</h1>
        <div className="of-settings-hero__subtitle">
          Gestiona el equipo clínico de la sede activa y controla el uso del plan.
        </div>
      </div>

      {isReadOnly && (
        <div className="of-settings-banner of-settings-banner--danger">
          {readOnlyReason || "La clínica está en modo solo lectura."}
        </div>
      )}

      {error && (
        <div className="of-settings-banner of-settings-banner--danger">
          {error}
        </div>
      )}

      <div className="of-settings-grid">
        <div className="of-settings-card">
          <div className="of-settings-card__title">Capacidad médica actual</div>
          <div className="of-settings-card__subtitle">
            Estado de doctores en relación con el plan.
          </div>

          <div className="of-settings-summary">
            <div className="of-settings-pill">
              Doctores registrados: {doctorsCount}
            </div>
            <div className="of-settings-pill">
              Límite: {maxDoctors ?? "Ilimitado"}
            </div>
          </div>
        </div>

        <div className="of-settings-card">
          <div className="of-settings-card__title">Nuevo doctor</div>
          <div className="of-settings-card__subtitle">
            Crea un nuevo doctor para la clínica activa.
          </div>

          <div className="of-settings-grid--form">
            <div className="of-settings-field">
              <label className="of-settings-label">Nombre</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nombre"
                className="of-settings-input"
              />
            </div>

            <div className="of-settings-field">
              <label className="of-settings-label">Correo</label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                className="of-settings-input"
              />
            </div>

            <div className="of-settings-field">
              <label className="of-settings-label">Estado</label>
              <div className="of-settings-pill">Clínica activa</div>
            </div>

            <button
              onClick={handleCreate}
              disabled={!canCreate}
              className="of-settings-button of-settings-button--primary"
            >
              {saving ? "Guardando..." : "Crear"}
            </button>
          </div>

          {limitReached && (
            <div
              className="of-settings-banner of-settings-banner--warning"
              style={{ marginTop: 12, marginBottom: 0 }}
            >
              Has alcanzado el límite de doctores de tu plan.
            </div>
          )}
        </div>

        {!doctors.length ? (
          <div className="of-settings-empty">
            No hay doctores registrados todavía.
          </div>
        ) : (
          <div className="of-settings-list">
            {doctors.map((d) => (
              <div key={d.id} className="of-settings-item">
                <div className="of-settings-item__title">{d.name}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}